import { useMemo } from 'react'
import {
	FieldPath,
	FieldValues,
	useFormContext,
	useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { FormValues } from '../Calculator'
import { formConfig } from '../helpers/config'
import { createOptions } from '../helpers/options'
import { getData } from '../helpers/transform'
import Input from './Input'
import Select from './Select'
import { FieldContainer, FieldRow } from './styles'

interface FieldGroupProps {
	section: keyof FormValues
	index: number
	field: FieldValues
}

const FieldGroup = ({ section, index, field }: FieldGroupProps) => {
	const { t } = useTranslation()

	const { control } = useFormContext()

	const data = useWatch({
		control,
		defaultValue: field,
		name: `${section}.${index}`,
	})

	const optionsdata = useMemo(() => getData(), [])

	return (
		<FieldRow key={field.id}>
			{formConfig[section].map(
				({ type, fieldName, unit, options, dependentFields, disabled }) => {
					if (type === 'select') {
						return (
							<FieldContainer key={`${section}.${index}.${fieldName}`}>
								<Select
									fieldName={
										`${section}.${index}.${fieldName}` as FieldPath<FormValues>
									}
									label={t('calculator.form.input.label', {
										context: `${section}_${fieldName}`,
									})}
									dependentFields={{
										prefix: `${section}.${index}`,
										fields: dependentFields,
									}}
									options={createOptions({
										optionsdata,
										formdata: data,
										path: options,
									})}
								/>
							</FieldContainer>
						)
					}

					if (type === 'number') {
						return (
							<FieldContainer
								key={`${section}.${index}.${fieldName}`}
								pt={0.75}
							>
								<Input
									fieldName={
										`${section}.${index}.${fieldName}` as FieldPath<FormValues>
									}
									label={t('calculator.form.input.label', {
										context: fieldName,
									})}
									disabled={disabled}
									unit={unit}
									number
								/>
							</FieldContainer>
						)
					}

					return null
				}
			)}
		</FieldRow>
	)
}

export default FieldGroup
